import React from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { useChat } from "../lib/chatContext";

/**
 * AskRailButton — replaces the older "<Link to=/chat?q=...>שאל AI</Link>" pattern.
 *
 * Clicking it sends the question to the persistent AI rail (right side panel)
 * via the `ai-copilot-ask` window event, instead of navigating away to a full
 * chat page. The user can still click "פתח צ׳אט מלא" inside the rail to
 * migrate the conversation to the full /chat page.
 *
 * Props:
 *   - question:  The natural-language prompt sent to the AI.
 *   - context:   { label, sublabel? } — small chip shown above the user
 *                bubble so it's instantly clear which row/SKU triggered it.
 *   - label:     Visible button label (default "בדוק עם AI").
 *   - testId:    data-testid override.
 *
 * The button is automatically disabled while the AI is mid-response — clicking
 * 5 times in 2 seconds will no longer queue 5 redundant questions.
 */
export default function AskRailButton({
  question,
  context = null,
  label = "בדוק עם AI",
  className = "",
  variant = "default",
  testId = "ask-rail-btn",
}) {
  const { loading } = useChat();

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    window.dispatchEvent(
      new CustomEvent("ai-copilot-ask", {
        detail: { prompt: question, context },
      })
    );
  };

  const base = "inline-flex items-center gap-1 text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
  const styles =
    variant === "ghost"
      ? "text-primary hover:text-primary/80"
      : "text-primary hover:text-primary/80 hover:underline underline-offset-2";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`${base} ${styles} ${className}`}
      data-testid={testId}
      title={loading ? "ה-AI עדיין עונה על השאלה הקודמת..." : undefined}
    >
      <Sparkles className="w-3 h-3" strokeWidth={2} />
      <span>{label}</span>
      <ArrowRight className="w-3 h-3" strokeWidth={2} />
    </button>
  );
}
